import React, { useEffect, useState, useMemo, useRef } from "react";
import { confirm } from "../utils/confirm";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import ScadenzePersonaleWidget, { calcolaAvvisiPersonale } from "../components/ScadenzePersonaleWidget";
import { logEvento, TIPI_EVENTO } from "../utils/logEvento";
import { logModifica, CAMPI_LABEL } from "../utils/logModifica";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Plus, X, Users, Check } from "lucide-react";
import { parseValutaItaliana, formatValutaItaliana } from "../utils/clienteHelpers";
import ImportaExcelModal from "../components/ImportaExcelModal";
import SchedaDipendentePage from "./SchedaDipendentePage";
import { RUOLI_OPERATIVI } from "../utils/permessiDefaultPerRuolo";
import { bloccaSeReadOnly } from "../utils/trialGate";
import { toLocalDateStr } from "../utils/dateUtils";
import { numIt } from "../utils/economiaCommessa";

const TIPI = [
  { id:"dipendente",     label:"Dipendente",     icon:"👷", colore:"#7c3aed" },
  { id:"subappaltatore", label:"Subappaltatore",  icon:"🏗️", colore:"#0891b2" },
  { id:"professionista", label:"Professionista",  icon:"💼", colore:"#059669" },
];

const MANSIONI = [
  "Titolare","Amministratore","Project Manager","Capocantiere",
  "Elettricista","Tecnico","Operaio","Magazziniere","Ufficio Acquisti",
  "Progettista","Architetto","Consulente","Altro"
];

// Categoria operativa — determina chi viene considerato nel simulatore disponibilità
// (da non confondere con "Ruolo", la colonna sola-lettura derivata dalle Abilitazioni)
const RUOLI = [
  "Operativo",      // Entra nel simulatore disponibilità maestranze
  "Non operativo",  // Non entra nel simulatore (PM, admin, direzione, ecc.)
];

// Cella inline editabile
function CellaEdit({ value, type="text", options, onSave, onCancel, placeholder="" }) {
  const [val, setVal] = useState(value||"");
  const ref = useRef();
  useEffect(()=>{ ref.current?.focus(); if(ref.current?.select) ref.current.select(); },[]);

  const save = () => onSave(val.trim());
  const handleKey = e => {
    if (e.key==="Enter") save();
    if (e.key==="Escape") onCancel();
  };

  if (options) return (
    <select ref={ref} value={val} onChange={e=>{setVal(e.target.value);onSave(e.target.value);}}
      onBlur={onCancel} onKeyDown={handleKey}
      style={{width:"100%",padding:"4px 6px",border:"2px solid #7c3aed",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"}}>
      <option value="">—</option>
      {options.map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <div style={{display:"flex",gap:4,alignItems:"center"}}>
      <input ref={ref} type={type} value={val} onChange={e=>{ setVal(e.target.value); if(type==="date") onSave(e.target.value); }}
        onBlur={save} onKeyDown={handleKey} placeholder={placeholder}
        min={type==="date"?"1900-01-01":undefined}
        max={type==="date"?toLocalDateStr(new Date()):undefined}
        style={{flex:1,padding:"4px 6px",border:"2px solid #7c3aed",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",minWidth:0}}/>
      {/* Il bottone di conferma serve solo per il testo libero: su una data il selettore
          del browser conferma già scegliendo il giorno, e il tasto occupava spazio
          allargando inutilmente la colonna. */}
      {type!=="date" && (
        <button onMouseDown={e=>{e.preventDefault();save();}}
          style={{background:"#7c3aed",border:"none",borderRadius:5,padding:"3px 6px",cursor:"pointer",color:"#fff",display:"flex",alignItems:"center",flexShrink:0}}>
          <Check size={11}/>
        </button>
      )}
    </div>
  );
}

// Cella visualizzazione — click per editare
function Cella({ value, display, canEdit, onEdit, placeholder="—", style={} }) {
  return (
    <div onClick={canEdit?onEdit:undefined}
      style={{padding:"2px 4px",minHeight:28,cursor:canEdit?"pointer":"default",borderRadius:4,
        color:value?"#111827":"#d1d5db",fontSize:12,...style,
        background:"transparent",transition:"background 0.1s"}}
      onMouseOver={e=>{if(canEdit)e.currentTarget.style.background="#f3f4f6";}}
      onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>
      {display||value||placeholder}
    </div>
  );
}

export default function DipendentiPage({ deepLink, onNavigate }) {
  const [schedaDip, setSchedaDip] = useState(null);
  const { isOrgAdmin, isPlatformOwner, userProfile, currentUser, isReadOnly } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const canEdit = isOrgAdmin || isPlatformOwner;
  const canSee = useCanSee();
  const puoVedereScadenze = isOrgAdmin || isPlatformOwner || canSee("scadenze-personale");
  // Permesso specifico e più ampio SOLO per l'assegnazione del ruolo aziendale — compito
  // organizzativo/HR che apriamo anche a Gestione e Amministrazione, senza allargare il resto
  // della tabella (mansione, costo orario, ecc.) che resta riservato a Titolare/Admin.
  const puoAssegnareRuolo = isOrgAdmin || isPlatformOwner ||
    ["gestione","amministrazione"].includes(userProfile?.ruoloOperativo);

  const [dipendenti, setDipendenti] = useState([]);

  // deepLink da EntityDrawer — pre-seleziona il dipendente (DOPO dipendenti)
  React.useEffect(() => {
    if (deepLink?.dipId && dipendenti.length > 0) {
      const dip = dipendenti.find(d => d.id === deepLink.dipId);
      if (dip) setSchedaDip(dip);
    }
  }, [deepLink?.dipId, dipendenti]);
  const [utentiNexora, setUtentiNexora] = useState([]);
  const [cellAttiva, setCellAttiva] = useState(null);
  // Scelta obbligatoria del ruolo al momento del collegamento — stesso identico pattern già
  // usato in Gestione Utenti quando si abilita un account senza ruolo: non si può completare
  // il collegamento di un Dipendente a un account privo di ruolo senza sceglierne uno.
  const [sceltaRuoloPerLink, setSceltaRuoloPerLink] = useState(null); // { dip, utente }
  const [ruoloSceltoLink, setRuoloSceltoLink] = useState("");
  const [nuovaRiga, setNuovaRiga] = useState(false);
  const [nuovoForm, setNuovoForm] = useState({ tipo:"dipendente", nome:"", cognome:"", dataNascita:"", codiceFiscale:"", mansione:"", ruolo:"", costoOrario:"", dataAssunzione:"", tipoContratto:"", livelloProfessionalita:"", nexoraUid:"", ruoloAccount:"" });
  const [salvandoDipendente, setSalvandoDipendente] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("tutti");
  const [tabAttivo, setTabAttivo] = useState("elenco");
  const [search, setSearch] = useState("");

  useEffect(()=>{
    if (!effectiveOrgId) return;
    const q2 = query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true));
    const u2 = onSnapshot(q2, async snap=>{
      const utenti = snap.docs.map(d=>d.data());
      setUtentiNexora(utenti);
      // Regole 74: l'anagrafica la modificano titolare e admin — il collegamento automatico lo fa solo chi può
      if (!canEdit) return;
      // Auto-collegamento: se un dipendente non ha nexoraUid, cerca utente con stesso nome
      const dipSnap = await import("firebase/firestore").then(({getDocs,query,collection,where})=>
        getDocs(query(collection(db,"dipendenti"),where("organizationId","==",effectiveOrgId)))
      );
      const dipList = dipSnap.docs.map(d=>({id:d.id,...d.data()}));
      for (const dip of dipList) {
        if (dip.nexoraUid) continue; // già collegato
        // Solo nome e cognome identici e un solo abbinamento possibile (W434): il collegamento
        // decide il costo orario delle ore di quell'account, un omonimo non va mai indovinato.
        const norm = (x) => String(x||"").trim().replace(/\s+/g," ").toLowerCase();
        const nomeCompleto = norm(`${dip.nome||""} ${dip.cognome||""}`);
        if (!dip.cognome || !nomeCompleto) continue;
        const candidati = utenti.filter(u => norm(u.displayName) === nomeCompleto);
        const omonimi = dipList.filter(x => norm(`${x.nome||""} ${x.cognome||""}`) === nomeCompleto);
        const match = candidati.length === 1 && omonimi.length === 1 && !dipList.some(x => x.nexoraUid === candidati[0].uid) ? candidati[0] : null;
        if (match) {
          // Se l'account trovato non ha ancora un ruolo (e non è il Titolare), l'auto-collegamento
          // si ferma qui — niente collegamento silenzioso senza ruolo: bisogna passare dal
          // collegamento manuale, che obbliga a sceglierlo prima di completare (vedi popup sotto).
          if (dip.tipo==="dipendente" && match.role!=="titolare" && !match.ruoloOperativo) continue;
          const { updateDoc, doc } = await import("firebase/firestore");
          await updateDoc(doc(db,"dipendenti",dip.id),{ nexoraUid: match.uid });
        }
      }
    });
    return u2;
  },[effectiveOrgId, canEdit]);

  useEffect(()=>{
    if (!effectiveOrgId) return;
    const q = query(collection(db,"dipendenti"),where("organizationId","==",effectiveOrgId));
    return onSnapshot(q,snap=>{
      const l = snap.docs.map(d=>({id:d.id,...d.data()}));
      l.sort((a,b)=>(a.idUnico||999)-(b.idUnico||999));
      setDipendenti(l);
    });
  },[effectiveOrgId]);

  const nScadenze = useMemo(()=>puoVedereScadenze ? calcolaAvvisiPersonale(dipendenti).length : 0, [dipendenti, puoVedereScadenze]);

  const nextId = useMemo(()=>{
    if (dipendenti.length===0) return 2;
    return Math.max(...dipendenti.map(d=>parseInt(d.idUnico)||0))+1;
  },[dipendenti]);

  const filtrati = dipendenti.filter(d=>{
    if (filtroTipo!=="tutti"&&d.tipo!==filtroTipo) return false;
    const nome=(d.nome||"")+" "+(d.cognome||"");
    if (search&&!nome.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const aggiorna = async (id, campo, valore) => {
    // Controllo duplicato CF se si modifica quel campo
    if (campo === "codiceFiscale" && valore?.trim()) {
      const cfClean = valore.trim().toUpperCase();
      const dup = dipendenti.find(d => d.id!==id && d.codiceFiscale?.trim().toUpperCase()===cfClean);
      if (dup) { addToast(`Codice Fiscale già presente: ${dup.nome} ${dup.cognome}`,"error"); setCellAttiva(null); return; }
    }
    const updates = { [campo]: valore, updatedAt: serverTimestamp() };
    // Gestione storico costo orario
    if (campo==="costoOrario") {
      const dip = dipendenti.find(d=>d.id===id);
      const costoOrario = parseValutaItaliana(valore);
      const vecchio = numIt(dip?.costoOrario)||0;
      if (costoOrario !== vecchio && costoOrario > 0) {
        const storico = dip?.costoOrarioStorico||[];
        const oggi = new Date().toISOString();
        const aggiornato = storico.map((s,i)=>i===storico.length-1&&s.al===null?{...s,al:oggi}:s);
        aggiornato.push({ valore:costoOrario, dal:oggi, al:null });
        updates.costoOrarioStorico = aggiornato;
        updates.costoOrario = costoOrario;
      } else {
        updates.costoOrario = costoOrario;
      }
    }
    // Aggiorna nomeCompleto se cambia nome o cognome
    if (campo==="nome"||campo==="cognome") {
      const dip = dipendenti.find(d=>d.id===id);
      const nome = campo==="nome" ? valore : (dip?.nome||"");
      const cognome = campo==="cognome" ? valore : (dip?.cognome||"");
      updates.nomeCompleto = `${nome} ${cognome}`.trim();
    }
    await updateDoc(doc(db,"dipendenti",id), updates);
    setCellAttiva(null);
  };

  const aggiungiDipendente = async () => {
    if (bloccaSeReadOnly(isReadOnly, addToast)) return;
    if (salvandoDipendente) return; // protezione doppio click — corretta oggi
    if (!nuovoForm.nome.trim()) { addToast("Nome obbligatorio — serve per riconoscerlo in ogni report e cartellino","error"); return; }
    if (!nuovoForm.cognome.trim()) { addToast("Cognome obbligatorio — serve per riconoscerlo in ogni report e cartellino","error"); return; }
    // Data di nascita: facoltativa alla creazione — serve solo per i documenti di sicurezza
    // sul lavoro, generati su richiesta in un secondo momento, mai in automatico. Diversa da
    // Costo orario e Categoria operativa, che restano bloccate: quelle toccano subito calcoli
    // economici e il Simulatore Disponibilità Maestranze, con un rischio di errore silenzioso
    // molto più concreto se lasciate vuote.
    if (!nuovoForm.costoOrario) { addToast("Costo orario obbligatorio — usato nei calcoli economici di ogni commessa a cui lavora","error"); return; }
    if (!nuovoForm.ruolo) { addToast("Categoria operativa obbligatoria — indica se la persona è Operativa o Non operativa, serve al Simulatore Disponibilità Maestranze","error"); return; }
    // Controllo duplicato Codice Fiscale (se inserito)
    if (nuovoForm.codiceFiscale?.trim()) {
      const cfClean = nuovoForm.codiceFiscale.trim().toUpperCase();
      const dup = dipendenti.find(d => d.codiceFiscale?.trim().toUpperCase() === cfClean);
      if (dup) { addToast(`Codice Fiscale già presente: ${dup.nome} ${dup.cognome}`,"error"); return; }
    }
    // Avviso omonimia se stesso nome+cognome+data nascita
    const omonimo = dipendenti.find(d =>
      d.nome?.trim().toLowerCase() === nuovoForm.nome.trim().toLowerCase() &&
      d.cognome?.trim().toLowerCase() === nuovoForm.cognome.trim().toLowerCase() &&
      d.dataNascita === nuovoForm.dataNascita
    );
    if (omonimo) { addToast(`Attenzione: esiste già ${omonimo.nome} ${omonimo.cognome} con la stessa data di nascita. Controlla prima di procedere.`,"error"); return; }
    // Account Nexiora scelto già nella riga nuova (W407): stesse regole del collegamento
    // dalle righe esistenti — un account privo di ruolo si collega solo scegliendone uno, se chi
    // inserisce può assegnare i ruoli (altrimenti si collega senza, come dalle righe esistenti: W415).
    const accountScelto = nuovoForm.nexoraUid ? utentiNexora.find(u => u.uid === nuovoForm.nexoraUid) : null;
    if (accountScelto && nuovoForm.tipo === "dipendente" && accountScelto.role !== "titolare" && !accountScelto.ruoloOperativo && !nuovoForm.ruoloAccount && puoAssegnareRuolo) {
      addToast(`Scegli il Ruolo di ${accountScelto.displayName}: senza ruolo il collegamento non si completa`, "error"); return;
    }
    setSalvandoDipendente(true);
    try {
      const costoOrario = parseValutaItaliana(nuovoForm.costoOrario);
      const { ruoloAccount, livelloProfessionalita, ...campi } = nuovoForm;
      if (accountScelto && ruoloAccount && ruoloAccount !== accountScelto.ruoloOperativo && accountScelto.role !== "titolare" && puoAssegnareRuolo)
        await updateDoc(doc(db,"users",accountScelto.uid), { ruoloOperativo: ruoloAccount, ultimaModificaDa: currentUser?.uid });
      await addDoc(collection(db,"dipendenti"),{
        ...campi,
        livelloProfessionalita: parseInt(livelloProfessionalita) || 0,
        nomeCompleto: `${nuovoForm.nome.trim()} ${nuovoForm.cognome.trim()}`,
        costoOrario,
        idUnico: nextId,
        attivo: true,
        organizationId: effectiveOrgId,
        costoOrarioStorico: costoOrario>0?[{valore:costoOrario,dal:new Date().toISOString(),al:null}]:[],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNuovaRiga(false);
      setNuovoForm({ tipo:"dipendente", nome:"", cognome:"", dataNascita:"", codiceFiscale:"", mansione:"", ruolo:"", costoOrario:"", dataAssunzione:"", tipoContratto:"", livelloProfessionalita:"", nexoraUid:"", ruoloAccount:"" });
      logEvento({ orgId:effectiveOrgId, tipo:"DIPENDENTE_CREATO", ...TIPI_EVENTO.DIPENDENTE_CREATO,
        titolo:`Nuovo collaboratore: ${nuovoForm.nome} ${nuovoForm.cognome}`,
        uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"",
        navigateTo:{page:"dipendenti"}
      });
      addToast("Dipendente aggiunto ✅","success");
    } finally {
      setSalvandoDipendente(false);
    }
  };

  const archivia = async id => {
    if (!await confirm("Disattivare questo dipendente?", { type:"danger" })) return;
    await updateDoc(doc(db,"dipendenti",id),{attivo:false,updatedAt:serverTimestamp()});
    addToast("Disattivato","info");
  };

  const TIPI_CONTRATTO = ['Tempo indeterminato', 'Tempo determinato', 'Apprendistato', 'A progetto', 'Somministrazione', 'Collaborazione coordinata', 'Partita IVA', 'Stage/Tirocinio', "Contratto d'opera", 'Intermittente', 'Part-time', 'Altro'];
  const LIVELLI_PROF = [
    { val:1, label:"1 — Inesperto",      col:"#6b7280" },
    { val:2, label:"2 — Poco esperto",   col:"#d97706" },
    { val:3, label:"3 — Autonomo",       col:"#2563eb" },
    { val:4, label:"4 — Esperto",        col:"#7c3aed" },
    { val:5, label:"5 — Molto esperto",  col:"#059669" },
  ];

  const accountSenzaScheda = utentiNexora.filter(u => u.uid && !dipendenti.some(d => d.nexoraUid === u.uid));

  const COLONNE = [
    { key:"idUnico",       label:"ID",            width:50,  readonly:true },
    { key:"tipo",          label:"Tipo",          width:120, options:TIPI.map(t=>t.id) },
    { key:"nome",          label:"Nome *",        width:120 },
    { key:"cognome",       label:"Cognome *",     width:120 },
    { key:"dataNascita",   label:"Data nascita",width:96, type:"date",
      hint:"Facoltativa — serve solo se genererai documenti di sicurezza sul lavoro per questa persona." },
    { key:"codiceFiscale", label:"Cod. Fiscale *", width:140 },
    { key:"mansione",        label:"Mansione",        width:130 },
    { key:"ruoloAziendale",  label:"Ruolo *",         width:170, special:"ruoloAziendale", readonly:true,
      hint:"Si assegna solo collegando l'account Nexiora del dipendente — non è compilabile qui, alla creazione della riga." },
    { key:"ruolo",           label:"Categoria operativa *", width:170, options:RUOLI, required:true,
      hint:"Operativo: squadre in cantiere, le ore sulle commesse arrivano dalla fonte dell'azienda (programmazione, rapportini o presenze). Non operativo: ufficio, PM, direzione — con l'account collegato ha «Le mie ore» e le sue ore vanno da sole nei costi delle commesse, al suo Costo/h." },
    { key:"livelloProfessionalita", label:"Livello", width:160, special:"livello" },
    { key:"costoOrario",     label:"Costo/h (€) *",  width:110 },
    { key:"dataAssunzione",  label:"Data assunzione", width:100, type:"date",
      hint:"Dal giorno di assunzione il cartellino e la nota spese richiedono le presenze. Se manca, si conta dalla creazione dell'account." },
    { key:"dataCessazione",  label:"Data cessazione", width:100, type:"date",
      hint:"Facoltativa — dopo questa data non sono più richieste presenze." },
    { key:"tipoContratto",   label:"Tipo contratto",  width:160, options:"TIPI_CONTRATTO" },
    { key:"finePeriodoProva", label:"Fine periodo prova", width:104, type:"date",
      hint:"Facoltativo — se compilata, Nexiora avvisa chi gestisce il personale con 30 giorni di anticipo." },
    { key:"scadenzaContratto", label:"Scadenza contratto", width:104, type:"date",
      hint:"Facoltativo — per contratti a termine. Nexiora avvisa con 60 giorni di anticipo." },
    { key:"nexoraUid",       label:"Collega account Nexiora", width:180, special:"nexoraLink" },
  ];

  const confermaLinkConRuolo = async () => {
    if (!ruoloSceltoLink) { addToast("Seleziona un ruolo","error"); return; }
    const { dip, utente } = sceltaRuoloPerLink;
    await updateDoc(doc(db,"users",utente.uid), { ruoloOperativo: ruoloSceltoLink, ultimaModificaDa: currentUser?.uid });
    await aggiorna(dip.id, "nexoraUid", utente.uid);
    addToast(`${utente.displayName} collegato con ruolo ${RUOLI_OPERATIVI.find(r=>r.id===ruoloSceltoLink)?.label} ✅`,"success");
    setSceltaRuoloPerLink(null);
  };

  if(schedaDip) return <SchedaDipendentePage dipendente={schedaDip} onBack={()=>setSchedaDip(null)} onNavigate={onNavigate}/>;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Scelta obbligatoria del ruolo al collegamento account */}
      {sceltaRuoloPerLink && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:14,padding:22,maxWidth:420,width:"90%"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <h3 style={{fontSize:15,fontWeight:800,color:"#1e1b4b"}}>Scegli il ruolo di {sceltaRuoloPerLink.utente.displayName}</h3>
              <button onClick={()=>setSceltaRuoloPerLink(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={18}/></button>
            </div>
            <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>
              L'account collegato non ha ancora un ruolo — è obbligatorio per completare il collegamento, altrimenti Controllo Economico e i Report Commessa non troveranno i suoi costi.
            </p>
            {RUOLI_OPERATIVI.map(r => (
              <button key={r.id} onClick={()=>setRuoloSceltoLink(r.id)}
                style={{width:"100%",textAlign:"left",padding:"10px 12px",borderRadius:9,border:`1.5px solid ${ruoloSceltoLink===r.id?"#7c3aed":"#e5e7eb"}`,background:ruoloSceltoLink===r.id?"#f5f3ff":"#fff",cursor:"pointer",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>
                <div style={{fontSize:13,fontWeight:700,color:ruoloSceltoLink===r.id?"#7c3aed":"#111827"}}>{r.label}</div>
                <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>{r.descrizione}</div>
              </button>
            ))}
            <button onClick={confermaLinkConRuolo} disabled={!ruoloSceltoLink}
              style={{width:"100%",marginTop:10,padding:"9px",borderRadius:9,border:"none",background:ruoloSceltoLink?"#7c3aed":"#e5e7eb",color:ruoloSceltoLink?"#fff":"#9ca3af",fontSize:13,fontWeight:700,cursor:ruoloSceltoLink?"pointer":"default"}}>
              Collega con questo ruolo
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
              <Users size={18} color="#7c3aed"/> Dipendenti & Collaboratori
              {canEdit && (
                <button onClick={()=>setNuovaRiga(true)} className="btn btn-primary"
                  style={{display:"flex",alignItems:"center",gap:6,fontSize:13,padding:"4px 12px"}}>
                  <Plus size={14}/> Aggiungi
                </button>
              )}
              {canEdit && (
                <ImportaExcelModal
                  label="Importa da Excel"
                  campiMappabili={[
                    {key:"nome",label:"Nome",indovina:/^nome$/},
                    {key:"cognome",label:"Cognome",indovina:/cognome/},
                    {key:"codiceFiscale",label:"Codice Fiscale",indovina:/cod.*fisc/},
                    {key:"dataNascita",label:"Data di nascita",indovina:/nascita/,tipo:"date"},
                    {key:"mansione",label:"Mansione",indovina:/mansione/},
                    {key:"costoOrario",label:"Costo orario",indovina:/costo.*orari|€\/h|euro.*ora/},
                    {key:"dataAssunzione",label:"Data assunzione",indovina:/assunzione/,tipo:"date"},
                    {key:"tipoContratto",label:"Tipo contratto",indovina:/contratto/},
                  ]}
                  campoObbligatorio="nome"
                  campiExtra={[
                    { key:"ruoloDefault", label:"Categoria operativa (vale per tutte le righe importate)", obbligatorio:true,
                      options:RUOLI },
                    { key:"livelloDefault", label:"Livello professionale (vale per tutte le righe importate)", obbligatorio:false,
                      options:LIVELLI_PROF.map(l=>l.label) },
                  ]}
                  avvisoIniziale="Funziona bene se il tuo file ha una riga per ogni persona, con i dati in colonne. Fogli con celle unite o strutture particolari potrebbero non importarsi correttamente — in quel caso conviene inserirli a mano la prima volta."
                  avvisoFinale="Categoria operativa e Livello non arrivano dal tuo file — sono dati che decidi tu, non un file esterno, e valgono per tutte le persone di questa importazione. Se il gruppo è misto (alcuni Operativi, altri no), importa in due gruppi separati, oppure correggi persona per persona dopo."
                  onConferma={async (righeMappate, valoriExtra) => {
                    const livelloScelto = LIVELLI_PROF.find(l=>l.label===valoriExtra.livelloDefault)?.val || 0;
                    let prossimoId = dipendenti.length===0 ? 2 : Math.max(...dipendenti.map(d=>parseInt(d.idUnico)||0))+1;
                    let creati = 0;
                    const saltate = []; // mancava Nome o Cognome — riga senza senso, non importata
                    const incomplete = []; // importata comunque, ma manca qualcosa da completare dopo
                    for (let i=0; i<righeMappate.length; i++) {
                      const r = righeMappate[i];
                      const numeroRiga = i + 2; // +2: riga 1 è l'intestazione del file
                      if (!r.nome?.trim() || !r.cognome?.trim()) {
                        saltate.push(`riga ${numeroRiga} (manca ${!r.nome?.trim()?"Nome":"Cognome"})`);
                        continue;
                      }
                      const mancano = [];
                      if (!r.dataNascita) mancano.push("Data di nascita");
                      if (!r.costoOrario || parseValutaItaliana(r.costoOrario)<=0) mancano.push("Costo orario");
                      if (mancano.length) incomplete.push(`${r.nome} ${r.cognome} (manca ${mancano.join(", ")})`);
                      const costoOrario = parseValutaItaliana(r.costoOrario||"0");
                      await addDoc(collection(db,"dipendenti"),{
                        tipo:"dipendente", nome:r.nome||"", cognome:r.cognome||"", codiceFiscale:r.codiceFiscale||"",
                        dataNascita:r.dataNascita||"", mansione:r.mansione||"", ruolo:valoriExtra.ruoloDefault,
                        livelloProfessionalita: livelloScelto,
                        costoOrario, dataAssunzione:r.dataAssunzione||"", tipoContratto:r.tipoContratto||"",
                        nomeCompleto: `${(r.nome||"").trim()} ${(r.cognome||"").trim()}`,
                        idUnico: prossimoId, attivo:true, organizationId: effectiveOrgId,
                        costoOrarioStorico: costoOrario>0?[{valore:costoOrario,dal:new Date().toISOString(),al:null}]:[],
                        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
                      });
                      prossimoId++;
                      creati++;
                    }
                    if (creati>0) addToast(`${creati} persona/e importate ✅ — collega ciascuna al proprio account Nexiora e assegna il Ruolo quando pronto`,"success");
                    if (incomplete.length>0) addToast(`${incomplete.length} importate con dati incompleti da completare: ${incomplete.slice(0,3).join(" · ")}${incomplete.length>3?` e altre ${incomplete.length-3}`:""}`,"info");
                    if (saltate.length>0) addToast(`${saltate.length} riga/e saltate perché senza Nome o Cognome: ${saltate.slice(0,3).join(" · ")}${saltate.length>3?` e altre ${saltate.length-3}`:""}`,"error");
                  }}
                />
              )}
            </h2>
            <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
              Clicca su una cella per modificarla · * campi obbligatori · Il costo orario storico non modifica mai i calcoli precedenti
            </div>
          </div>
        </div>
        {/* Banner ruolo */}
        <div style={{marginTop:10,background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"8px 14px",fontSize:11,color:"#92400e"}}>
          ⚠️ <strong>Importante:</strong> il campo <strong>Categoria operativa *</strong> è obbligatorio. Imposta <strong>Operativo</strong> per operai e tecnici di campo — solo loro entrano nel Simulatore Disponibilità Maestranze. Imposta <strong>Non operativo</strong> per PM, amministrativi, direzione e consulenti: con l'account collegato hanno <strong>«Le mie ore»</strong> e le ore segnate lì vanno da sole nei costi delle commesse, al loro Costo/h. La Mansione è libera: scrivi quella che preferisci.
        </div>

        {/* Banner Nexiora — Ruolo */}
        <div style={{marginTop:8,background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"8px 14px",fontSize:11,color:"#5b21b6"}}>
          💜 <strong>Nexiora:</strong> se Titolare o chi gestisce l'azienda partecipa a commesse tracciate economicamente, va inserito anche qui e collegato al proprio account — altrimenti Controllo Economico e i Report Commessa non trovano i suoi costi. Il <strong>Ruolo *</strong> è obbligatorio per ogni Dipendente collegato a un account: se manca, il collegamento non si completa finché non lo scegli. Assegnabile da qui (Titolare, Admin, Gestione, Amministrazione); tutto il resto delle abilitazioni si gestisce in Abilitazioni e Gestione Utenti.
        </div>
      </div>

      {/* Tab — elenco persone / scadenze contrattuali */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"0 24px",display:"flex",gap:2,flexShrink:0}}>
        {[{id:"elenco",label:"👥 Elenco persone"},
          ...(puoVedereScadenze ? [{id:"scadenze",label:`📄 Scadenze contrattuali${nScadenze>0?` (${nScadenze})`:""}`}] : [])
        ].map(t=>(
          <button key={t.id} onClick={()=>setTabAttivo(t.id)}
            style={{padding:"10px 16px",border:"none",background:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif",
              fontSize:13,fontWeight:tabAttivo===t.id?700:500,color:tabAttivo===t.id?"#7c3aed":"#6b7280",
              borderBottom:`2px solid ${tabAttivo===t.id?"#7c3aed":"transparent"}`}}>
            {t.label}
          </button>
        ))}
      </div>

      {tabAttivo==="scadenze" ? (
        <div style={{flex:1,overflowY:"auto",padding:24}}>
          <ScadenzePersonaleWidget onNavigate={onNavigate} vistaEstesa/>
        </div>
      ) : (<>

      {/* Filtri */}
      <div style={{background:"#f9fafb",borderBottom:"1px solid #e5e7eb",padding:"8px 24px",display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
        <input className="input" value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Cerca per nome..." style={{maxWidth:200}}/>
        {["tutti",...TIPI.map(t=>t.id)].map(tipo=>{
          const obj=TIPI.find(t=>t.id===tipo);
          return (
            <button key={tipo} onClick={()=>setFiltroTipo(tipo)}
              style={{padding:"4px 12px",borderRadius:7,border:`1px solid ${filtroTipo===tipo?"#7c3aed":"#e5e7eb"}`,
                background:filtroTipo===tipo?"#7c3aed":"#fff",color:filtroTipo===tipo?"#fff":"#6b7280",
                fontSize:12,fontWeight:filtroTipo===tipo?600:400,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              {tipo==="tutti"?"Tutti":obj?.icon+" "+obj?.label}
            </button>
          );
        })}
      </div>

      {/* Account senza scheda (W434): senza scheda non c'è costo orario, quindi le ore di
          quell'account non possono andare sulle commesse e «Le mie ore» non si attiva. */}
      {canEdit && accountSenzaScheda.length>0 && (
        <div role="status" style={{background:"#fff7ed",borderBottom:"1px solid #fed7aa",padding:"8px 24px",fontSize:12.5,color:"#9a3412",flexShrink:0}}>
          <b>👤 {accountSenzaScheda.length} account senza scheda:</b> {accountSenzaScheda.map(u=>u.displayName||u.email||"senza nome").join(" · ")}
          <div style={{fontSize:11.5,color:"#c2410c",marginTop:2}}>Le loro ore non possono andare sui costi delle commesse. Premi «Aggiungi», compila la scheda (Categoria operativa e Costo/h) e collega l'account nell'ultima colonna.</div>
        </div>
      )}

      {/* Tabella */}
      <div style={{flex:1,overflowX:"auto",overflowY:"auto"}}>
        <table style={{borderCollapse:"collapse",minWidth:"100%"}}>
          <thead style={{position:"sticky",top:0,zIndex:10}}>
            <tr style={{background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}>
              {COLONNE.map(col=>(
                <th key={col.key} style={{padding:"10px 12px",textAlign:"left",fontSize:11,fontWeight:700,
                  color:"#6b7280",whiteSpace:"nowrap",width:col.width,minWidth:col.width}}>
                  {col.label}
                  {col.hint && (
                    <span title={col.hint} style={{marginLeft:4,cursor:"help",color:"#9ca3af",fontWeight:400}}>ⓘ</span>
                  )}
                </th>
              ))}
              <th style={{width:40}}/>
            </tr>
          </thead>
          <tbody>
            {/* Nuova riga */}
            {nuovaRiga && (
              <tr style={{background:"#faf5ff",borderBottom:"1px solid #e9d5ff"}}>
                <td style={{padding:"6px 8px",fontSize:12,color:"#9ca3af",fontWeight:700}}>#{nextId}</td>
                {COLONNE.filter(col=>col.key!=="idUnico").map(col=>{
                  // Campi "special" (Ruolo aziendale, Livello, Collega account) non si possono
                  // compilare alla creazione — richiedono un account già collegato o un dipendente
                  // già esistente. Cella vuota disabilitata al loro posto, così tutte le colonne
                  // dopo restano allineate con l'intestazione — bug corretto oggi: prima questi
                  // campi mancavano del tutto dall'elenco, e ogni colonna successiva scivolava
                  // di una posizione.
                  // Livello, account e ruolo si scelgono già qui (W407): prima la riga nuova li
                  // mostrava come «—» e andavano impostati dopo, riga per riga.
                  if (col.special === "livello") return (
                    <td key={col.key} style={{padding:"4px 8px"}}>
                      <select className="input" aria-label="Livello" value={nuovoForm.livelloProfessionalita||""}
                        onChange={e=>setNuovoForm(f=>({...f,livelloProfessionalita:e.target.value}))} style={{fontSize:12,padding:"4px 6px"}}>
                        <option value="">— Non definito —</option>
                        {LIVELLI_PROF.map(l=><option key={l.val} value={l.val}>{l.label}</option>)}
                      </select>
                    </td>
                  );
                  if (col.special === "nexoraLink") return (
                    <td key={col.key} style={{padding:"4px 8px"}}>
                      <select className="input" aria-label="Collega account Nexiora" value={nuovoForm.nexoraUid||""}
                        onChange={e=>{ const uid=e.target.value; const u=utentiNexora.find(x=>x.uid===uid);
                          setNuovoForm(f=>({...f,nexoraUid:uid,ruoloAccount:u?.ruoloOperativo||""})); }} style={{fontSize:12,padding:"4px 6px"}}>
                        <option value="">— Nessun collegamento —</option>
                        {utentiNexora.filter(u=>!dipendenti.some(d=>d.nexoraUid===u.uid)).map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                      </select>
                    </td>
                  );
                  if (col.special === "ruoloAziendale") {
                    const acc = nuovoForm.nexoraUid ? utentiNexora.find(u=>u.uid===nuovoForm.nexoraUid) : null;
                    if (!acc || nuovoForm.tipo!=="dipendente") return (
                      <td key={col.key} style={{padding:"4px 8px",fontSize:10.5,color:"#9ca3af"}} title="Il ruolo si sceglie collegando l'account Nexiora (colonna «Collega account Nexiora»)">
                        {nuovoForm.tipo==="dipendente" ? "prima collega l'account →" : "—"}
                      </td>
                    );
                    if (acc.role==="titolare") return <td key={col.key} style={{padding:"4px 8px",fontSize:11,fontWeight:700,color:"#7c3aed"}}>Titolare</td>;
                    return (
                      <td key={col.key} style={{padding:"4px 8px"}}>
                        <select className="input" aria-label="Ruolo" value={nuovoForm.ruoloAccount||""} disabled={!puoAssegnareRuolo}
                          onChange={e=>setNuovoForm(f=>({...f,ruoloAccount:e.target.value}))} style={{fontSize:12,padding:"4px 6px"}}>
                          <option value="">— Scegli il ruolo —</option>
                          {RUOLI_OPERATIVI.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                        </select>
                      </td>
                    );
                  }
                  if (col.special || col.readonly) {
                    return <td key={col.key} style={{padding:"4px 8px",fontSize:11,color:"#d1d5db"}}>—</td>;
                  }
                  const opzioni = col.options==="TIPI_CONTRATTO" ? TIPI_CONTRATTO : col.options;
                  return (
                    <td key={col.key} style={{padding:"4px 8px"}}>
                      {opzioni ? (
                        <select className="input" value={nuovoForm[col.key]||""}
                          onChange={e=>setNuovoForm(f=>({...f,[col.key]:e.target.value}))}
                          style={{fontSize:12,padding:"4px 6px"}}>
                          <option value="">—</option>
                          {opzioni.map(o=><option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input className="input" type={col.type||"text"}
                          value={nuovoForm[col.key]||""}
                          onChange={e=>setNuovoForm(f=>({...f,[col.key]:e.target.value}))}
                          style={{fontSize:12,padding:"4px 6px"}}/>
                      )}
                    </td>
                  );
                })}
                <td style={{padding:"4px 8px"}}>
                  <div style={{display:"flex",gap:4}}>
                    <button onClick={aggiungiDipendente} disabled={salvandoDipendente}
                      style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",cursor:salvandoDipendente?"default":"pointer",fontSize:12,fontFamily:"'Inter',sans-serif",opacity:salvandoDipendente?0.6:1}}>
                      {salvandoDipendente?"...":"✓"}
                    </button>
                    <button onClick={()=>setNuovaRiga(false)}
                      style={{background:"#f3f4f6",color:"#6b7280",border:"none",borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* Righe esistenti */}
            {filtrati.length===0 && !nuovaRiga ? (
              <tr><td colSpan={COLONNE.length+1} style={{padding:48,textAlign:"center",color:"#9ca3af",fontSize:13}}>
                Nessun dipendente — clicca "Aggiungi" per inserire il primo
              </td></tr>
            ) : filtrati.map((d,ri)=>{
              const tipoObj=TIPI.find(t=>t.id===d.tipo)||TIPI[0];
              const inattivo=d.attivo===false;
              return (
                <tr key={d.id} style={{borderBottom:"1px solid #f3f4f6",background:inattivo?"#f9f9f9":ri%2===0?"#fff":"#fafafa",opacity:inattivo?0.5:1}}>
                  {COLONNE.map(col=>{
                    const isAttiva = cellAttiva?.id===d.id&&cellAttiva?.campo===col.key;
                    const editabile = canEdit&&!inattivo&&!col.readonly;
                    let display = d[col.key];
                    if (col.key==="tipo") display = <span style={{fontSize:11,fontWeight:700,color:tipoObj.colore}}>{tipoObj.icon} {tipoObj.label}</span>;
                    if (col.key==="dataNascita"&&d.dataNascita) display = new Date(d.dataNascita+"T00:00:00").toLocaleDateString("it-IT");
                    if (col.key==="dataAssunzione"&&d.dataAssunzione) display = new Date(d.dataAssunzione+"T00:00:00").toLocaleDateString("it-IT");
                    if (col.special==="livello") {
                      const livelloAttuale = parseInt(d.livelloProfessionalita)||0;
                      const livInfo = LIVELLI_PROF.find(l=>l.val===livelloAttuale);
                      const isAttivaLiv = cellAttiva?.id===d.id&&cellAttiva?.campo==="livelloProfessionalita";
                      if(isAttivaLiv) return (
                        <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle"}}>
                          <select className="input" defaultValue={livelloAttuale||""}
                            onChange={async e=>{ await aggiorna(d.id,"livelloProfessionalita",parseInt(e.target.value)||0); setCellAttiva(null); }}
                            onBlur={()=>setCellAttiva(null)}
                            style={{fontSize:11,padding:"3px 5px"}} autoFocus>
                            <option value="">— Non definito —</option>
                            {LIVELLI_PROF.map(l=><option key={l.val} value={l.val}>{l.label}</option>)}
                          </select>
                        </td>
                      );
                      return (
                        <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle",cursor:"pointer"}}
                          onClick={()=>editabile&&setCellAttiva({id:d.id,campo:"livelloProfessionalita"})}
                          onMouseOver={e=>e.currentTarget.style.background="#f3f4f6"}
                          onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                          {livInfo
                            ? <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:8,background:livInfo.col+"22",color:livInfo.col,whiteSpace:"nowrap"}}>{livInfo.label}</span>
                            : <span style={{fontSize:11,color:"#d1d5db"}}>— Non definito</span>}
                        </td>
                      );
                    }
                    if (col.special==="ruoloAziendale") {
                      const uLinked = utentiNexora.find(u=>u.uid===d.nexoraUid);
                      let testo = "—", colore = "#d1d5db";
                      if (uLinked) {
                        if (uLinked.role==="titolare") { testo = "Titolare"; colore = "#7c3aed"; }
                        else {
                          const r = RUOLI_OPERATIVI.find(r=>r.id===uLinked.ruoloOperativo);
                          testo = r?.label || "—"; colore = r ? "#2563eb" : "#d1d5db";
                        }
                      }
                      // Modificabile solo per Dipendenti veri (non Subappaltatori/Professionisti,
                      // per cui il concetto di ruolo aziendale non si applica), collegati a un
                      // account, e con permesso — il Titolare non si tocca mai da qui, si protegge
                      // da solo come già avviene in Gestione Utenti.
                      const puoModificareQuestaRiga = puoAssegnareRuolo && d.tipo==="dipendente" && uLinked && uLinked.role!=="titolare";
                      const isAttivaRuolo = cellAttiva?.id===d.id&&cellAttiva?.campo==="ruoloAziendale";
                      if (isAttivaRuolo) return (
                        <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle"}}>
                          <select className="input" defaultValue={uLinked.ruoloOperativo||""}
                            onChange={async e=>{
                              const nuovoRuolo = e.target.value;
                              if (!nuovoRuolo) { setCellAttiva(null); return; }
                              await updateDoc(doc(db,"users",uLinked.uid), { ruoloOperativo: nuovoRuolo, ultimaModificaDa: currentUser?.uid });
                              addToast(`Ruolo di ${uLinked.displayName} aggiornato ✅`,"success");
                              setCellAttiva(null);
                            }}
                            onBlur={()=>setCellAttiva(null)}
                            style={{fontSize:11,padding:"3px 5px"}} autoFocus>
                            <option value="">— Nessun ruolo —</option>
                            {RUOLI_OPERATIVI.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                          </select>
                        </td>
                      );
                      return (
                        <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle",cursor:puoModificareQuestaRiga?"pointer":"default"}}
                          onClick={()=>{
                            if (puoModificareQuestaRiga) setCellAttiva({id:d.id,campo:"ruoloAziendale"});
                            else if (puoAssegnareRuolo && d.tipo==="dipendente" && !uLinked) addToast("Il Ruolo è quello dell'account Nexiora: collega prima l'account nella colonna «Collega account Nexiora», poi scegli il ruolo","info");
                          }}
                          onMouseOver={e=>{if(puoModificareQuestaRiga)e.currentTarget.style.background="#f3f4f6";}}
                          onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:11,fontWeight:uLinked?700:400,color:colore}}>{testo}</span>
                        </td>
                      );
                    }
                    if (col.special==="nexoraLink") {
                      const uLinked = utentiNexora.find(u=>u.uid===d.nexoraUid);
                      const isAttivaNex = cellAttiva?.id===d.id&&cellAttiva?.campo==="nexoraUid";
                      if (isAttivaNex) return (
                        <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle"}}>
                          <select className="input" defaultValue={d.nexoraUid||""}
                            onChange={async e=>{
                              const nuovoUid = e.target.value;
                              setCellAttiva(null);
                              if (!nuovoUid) { await aggiorna(d.id,"nexoraUid",""); return; }
                              const utenteScelto = utentiNexora.find(u=>u.uid===nuovoUid);
                              const giaCollegato = dipendenti.find(x=>x.id!==d.id && x.nexoraUid===nuovoUid);
                              if (giaCollegato) { addToast(`L'account di ${utenteScelto?.displayName||""} è già collegato a ${giaCollegato.nome} ${giaCollegato.cognome}: scollegalo prima da lì`,"error"); return; }
                              // Un Dipendente collegato a un account privo di ruolo resta "invisibile"
                              // al resto di Nexiora (Controllo Economico, Report, ecc.) — per questo il
                              // collegamento non si completa finché non si sceglie anche il ruolo.
                              if (d.tipo==="dipendente" && utenteScelto && utenteScelto.role!=="titolare" && !utenteScelto.ruoloOperativo && puoAssegnareRuolo) {
                                setSceltaRuoloPerLink({ dip:d, utente:utenteScelto });
                                setRuoloSceltoLink("");
                                return;
                              }
                              await aggiorna(d.id,"nexoraUid",nuovoUid);
                            }}
                            onBlur={()=>setCellAttiva(null)}
                            style={{fontSize:11,padding:"3px 5px"}} autoFocus>
                            <option value="">— Nessun collegamento —</option>
                            {utentiNexora.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                          </select>
                        </td>
                      );
                      display = uLinked
                        ? <span style={{fontSize:11,color:"#059669",fontWeight:600}}>✓ {uLinked.displayName}</span>
                        : <span style={{fontSize:11,color:"#9ca3af"}}>Non collegato</span>;
                      return (
                        <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle",cursor:"pointer"}}
                          onClick={()=>setCellAttiva({id:d.id,campo:"nexoraUid"})}
                          onMouseOver={e=>e.currentTarget.style.background="#f3f4f6"}
                          onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                          {display}
                        </td>
                      );
                    }
                    if (col.key==="costoOrario"&&d.costoOrario>0) display = <span style={{fontWeight:800,color:tipoObj.colore}}>€ {formatValutaItaliana(d.costoOrario)}/h</span>;
                    if (col.key==="idUnico") {
                      const incompleto = d.tipo==="dipendente" && (!d.dataNascita || !(d.costoOrario>0));
                      display = (
                        <span style={{fontSize:11,fontWeight:700,color:"#fff",background:tipoObj.colore,padding:"2px 6px",borderRadius:8,display:"inline-flex",alignItems:"center",gap:4}}>
                          #{d.idUnico}
                          {incompleto && <span title="Dati incompleti: manca Data di nascita e/o Costo orario" style={{fontSize:10}}>⚠️</span>}
                        </span>
                      );
                    }
                    const colOptions = col.options==="TIPI_CONTRATTO" ? TIPI_CONTRATTO : col.options;
                    return (
                      <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle"}}>
                        {isAttiva ? (
                          <CellaEdit value={col.key==="costoOrario"?String(d.costoOrario||""):d[col.key]||""}
                            type={col.type||"text"} options={colOptions}
                            onSave={v=>aggiorna(d.id,col.key,v)}
                            onCancel={()=>setCellAttiva(null)}/>
                        ) : (
                          <Cella value={d[col.key]} display={display} canEdit={editabile}
                            onEdit={()=>setCellAttiva({id:d.id,campo:col.key})}/>
                        )}
                      </td>
                    );
                  })}
                  <td style={{padding:"4px 8px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <button onClick={()=>setSchedaDip(d)} title="Apri scheda personale"
                        style={{background:"none",border:"1px solid #e9d5ff",borderRadius:5,cursor:"pointer",color:"#7c3aed",padding:"2px 6px",fontSize:10,fontWeight:700,fontFamily:"'Inter',sans-serif"}}
                        onMouseOver={e=>e.currentTarget.style.background="#ede9fe"}
                        onMouseOut={e=>e.currentTarget.style.background="none"}>
                        Scheda
                      </button>
                      {canEdit&&!inattivo&&(
                        <button onClick={()=>archivia(d.id)} title="Disattiva"
                          style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db",padding:4,display:"flex",alignItems:"center"}}
                          onMouseOver={e=>e.currentTarget.style.color="#dc2626"}
                          onMouseOut={e=>e.currentTarget.style.color="#d1d5db"}>
                          <X size={13}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}
